import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { BillingRuleRecord } from '@ps/user-settings-models';

import { AllBuyerSettings, CompanySettings } from '../models/settings.model';
import {
  BuyerSettingsResponse,
  CompanySettingsResponse,
} from './dtos/settings.dto';
import { CurrencySettingsMapper } from './mappers/currency-settings.mapper';
import { CurrencySettingsResponseDto } from './dtos/currency-settings.dto';
import { CurrencySettings } from '../models/currency-settings.model';
import { SettingsMapper } from './mappers/settings.mapper';

@Injectable({ providedIn: 'root' })
export class SettingsApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = `${this._env.pureSpectrum.url}`;

  private readonly _mapper = new SettingsMapper();
  private readonly _currencyMapper = new CurrencySettingsMapper();

  public getAllBuyerSettings(): Observable<AllBuyerSettings> {
    return this._http
      .get<BuyerSettingsResponse>(`${this._baseUrl}/buyerConfigSettings`)
      .pipe(map((response) => this._mapper.fromDto(response.data)));
  }

  public getCompanySettings(
    companyId: number,
  ): Observable<CompanySettings | null> {
    return this._http
      .get<CompanySettingsResponse>(`${this._baseUrl}/company/${companyId}`)
      .pipe(map((response) => this._mapper.fromCompanyDto(response.company)));
  }

  public getCurrencySettings(fx: number): Observable<CurrencySettings> {
    return this._http
      .get<CurrencySettingsResponseDto>(
        `${this._baseUrl}/international_currency/${fx}?UI=1`,
      )
      .pipe(
        map((response) => this._currencyMapper.fromDto(response.currencies)),
      );
  }

  public getBillingRules(): Observable<BillingRuleRecord> {
    return this._http.get<BillingRuleRecord>(
      `${this._baseUrl}/buyers/v2/billing-rules`,
    );
  }

  public getBillingRulesByCompany(
    companyId: number,
  ): Observable<BillingRuleRecord> {
    return this._http.get<BillingRuleRecord>(
      `${this._baseUrl}/buyers/v2/billing-rules/${companyId}`,
    );
  }
}
